/**
 * Generation API types for the book generation pipeline.
 * Used by both client (polling) and server (status tracking).
 */
import { z } from 'zod';
import { PAGE_BORDER_STYLES } from './borderDecorations.js';
import { lastRegenFailureSchema } from './regeneration.js';
import type { FailureDetails } from './orderProjection.js';

/**
 * Steps in the generation pipeline, mapped from LangGraph node names.
 */
export const GENERATION_STEPS = [
  'starting',
  'generating_plan',
  'plan_review',
  'human_plan_review',
  'generating_character_sheet',
  'human_character_sheet_review',
  'generating_storyboard',
  'generating_pages',
  'generating_images',
  'generating_cover',
  'generating_pdf',
  'completed',
  'failed',
] as const;

export type GenerationStep = (typeof GENERATION_STEPS)[number];

/**
 * Coarse-grained pipeline phase tag attached to LangGraph metadata for
 * observability (LangSmith filtering, Sentry span attributes, structured logs).
 *
 * Phases are coarser than `GenerationStep` — they describe which segment of the
 * pipeline the run is entering, not the precise step. Used by Phase 0a
 * observability fixes (see `docs/architecture/phase-0-decisions.md`).
 */
export const GENERATION_PHASES = [
  'plan',
  'character_sheet',
  'pages',
  'cover',
  'pdf',
  'recovery',
] as const;

export type GenerationPhase = (typeof GENERATION_PHASES)[number];

/** All possible failure reason values (single source of truth) */
export const FAILURE_REASONS = [
  'CONTENT_BLOCKED',
  'PLAN_GENERATION_FAILED',
  'CHARACTER_SHEET_FAILED',
  // Cross-page-consistency storyboard — terminal failure of the storyboard
  // generation phase (mirrors `CHARACTER_SHEET_FAILED`). Emitted by the
  // storyboard skill / MOCK_MODE force-fail trigger (`__FORCE_STORYBOARD_FAIL__`)
  // when storyboard generation cannot produce a usable artifact. Maps to the
  // `survey_caused` context, same as the character-sheet failure.
  'STORYBOARD_GENERATION_FAILED',
  'PAGE_GENERATION_FAILED',
  'COVER_GENERATION_FAILED',
  'PDF_GENERATION_FAILED',
  // Issue #633 Task 8 — emitted by the supernode's `loopDetectionHook`
  // when the agent repeats the same tool call ≥5 times in a sliding
  // window. The hook throws a `PIPELINE_FAILED:LOOP_DETECTED:transient`
  // envelope so the route layer's `parsePipelineFailureError` writes
  // the correct slug instead of falling back to
  // `PAGE_GENERATION_FAILED:transient`.
  'LOOP_DETECTED',
  // Issue #677 — fine-grained reasons emitted by the page-gen / cover
  // subagents via the new `AgentError` taxonomy
  // (`apps/server/src/services/errors/AgentError.ts`). These
  // exist so the route layer's `parsePipelineFailureError` can write a
  // specific slug into `orders.failure_reason` instead of the catch-all
  // `PAGE_GENERATION_FAILED`. They map onto the same five existing
  // `FAILURE_CONTEXTS` (no new contexts) via `CATEGORY_TO_CONTEXT`.
  'PIXEL_LIMIT_EXCEEDED',
  'PAGE_COUNT_MISMATCH',
  'PAGE_IMAGE_EMPTY',
  'GEMINI_SAFETY_BLOCK',
  'RECURSION_LIMIT_EXCEEDED',
  'GLOBAL_REGEN_BUDGET_EXHAUSTED',
  'PLAN_INVALID_PAGE_COUNT',
  // Issue #713 Task 2 — emitted by `generateStoryPlan`'s post-invoke
  // validator when Gemini returns a `pagePlan` entry with an empty
  // `setting` or `illustration` (Zod allows empty strings on
  // `z.string()`). Mirrors `PLAN_INVALID_PAGE_COUNT` as a COUNT_MISMATCH
  // category — retryable so the LangGraph retry policy re-runs the node.
  'PLAN_INVALID_FIELDS',
  'COMPOSITOR_FAILED',
  'GEMINI_QUOTA_EXCEEDED',
  'GEMINI_TRANSIENT',
  // Issue #720 — emitted by `generatePagesNode` when the handoff
  // `planJson` arrives as malformed JSON (e.g. Gemini appended
  // explanation prose after the closing `}`). The page-gen node first
  // attempts a repair pass (slice to the last top-level `}`); only if
  // that also fails do we throw with this reason. Classified as
  // TRANSIENT so the LangGraph node `retryPolicy` re-runs the node
  // instead of giving up at `globalRegenCount=0`.
  'PLAN_JSON_PARSE_FAILED',
  // Issue #888 D1 — the model spent its entire completion budget and
  // returned a truncated (or empty) response. Emitted by the agent-loop
  // recovery hook after MAX_MALFORMED_RETRIES consecutive truncations, and
  // by `callAzureVisionJudge` when a judge turn is cut short mid-JSON.
  //
  // The measured shape on orders `d5d8e9eb` / `ea18d0e0` (2026-08-25):
  // `finish_reason: "length"`, `output_tokens: 32768`, of which
  // `reasoning: 32768` — the whole visible-output budget went to reasoning
  // and the turn carried no tool call at all.
  //
  // 🚨 TRANSIENT / retryable on purpose. This is genuinely transient model
  // behaviour, unlike the #866/#869 permanent classes — a retry can and does
  // succeed. A non-retryable verdict here would kill a paid book on a
  // hiccup and ship retry-inviting copy on a verdict that forbids a retry.
  'MODEL_OUTPUT_TRUNCATED',
  // Issue #724 phase 1 — typed reasons emitted by skills when their raw
  // `throw new Error(...)` sites were converted to typed `AgentError`s.
  // Kept aggressively normalised (a small handful of canonical slugs
  // shared across many throws) so the `inferFailureContext` switch in
  // `apps/server/src/routes/generation.ts` stays small.
  //
  //   - `PAGE_PLAN_MISSING_PAGES` (COUNT_MISMATCH, retryable) — the
  //     skill was asked to generate text for a `pageId` that the
  //     supplied `pagePlan` slice has no matching entry for. TC-9
  //     trigger in `generateStoryPage.ts`.
  //   - `TOOL_INPUT_MISSING_FIELD` (COUNT_MISMATCH, retryable) — the
  //     caller invoked a skill without one of the structured fields the
  //     skill's contract requires (e.g. `pageId` in real-mode page-image
  //     generation, `pageIds`/`pageId` for batch text generation).
  //   - `GEMINI_EMPTY_RESPONSE` (TRANSIENT, retryable) — Gemini returned
  //     no inline image data / no text candidate / no content. Shared
  //     by every image/text skill.
  //   - `INFRA_UPLOAD_FAILED` (TRANSIENT, retryable) — Supabase upload,
  //     signed-URL mint, or other infra round-trip failed.
  //   - `FIELD_LENGTH_EXCEEDED` (PERMANENT) — reserved for permanent
  //     validation failures on values we control (not yet wired; placeholder
  //     so the slug exists before downstream consumers reference it).
  //   - `JSON_PARSE_FAILED` (PERMANENT) — the skill received a string it
  //     could not parse / Zod-validate (storyOutlineJson, pageImagesJson,
  //     surveyJson).
  //   - `IMAGE_COMPOSITION_FAILED` (PERMANENT) — Sharp / imaging pipeline
  //     failure that we don't currently classify more specifically.
  //   - `UNEXPECTED_ERROR` (PERMANENT) — generic catch-all for sites that
  //     have no more specific category. Configuration-missing throws
  //     (e.g. `GOOGLE_CLOUD_PROJECT` unset at boot) also map here.
  'PAGE_PLAN_MISSING_PAGES',
  'TOOL_INPUT_MISSING_FIELD',
  'GEMINI_EMPTY_RESPONSE',
  'INFRA_UPLOAD_FAILED',
  'FIELD_LENGTH_EXCEEDED',
  'JSON_PARSE_FAILED',
  'IMAGE_COMPOSITION_FAILED',
  'UNEXPECTED_ERROR',
  // Issue #727 — emitted when a page-gen pass (or the pre-finalize
  // completeness gate) finds the accepted/force-accepted page set does
  // not cover every planned `pageId`. Carries
  // `details: { missingPages: string[], have: number, want: number }` so
  // the gap-fill re-handoff (and admin tooling) know exactly which pages
  // are missing. Classified via the `CAPACITY_EXCEEDED` category
  // (retryable → `transient` context): a gap-fill / Try Again can
  // complete the missing pages, so finalize never assembles an
  // incomplete set and the customer never sees a misleading
  // `StorageUnknownError`.
  'PAGE_SET_INCOMPLETE',
  // Issue #729 — emitted by the `withToolErrorBoundary` wrapper when the
  // supernode regenerates the character sheet > the consecutive-regen cap
  // (5) WITHOUT ever firing `requestCharacterSheetReview`. The CS subagent
  // is a one-shot `tool()` with no internal loop, and the regen loop is
  // ReAct-model-driven (the prompt only *advises* ~5 attempts), so a
  // misbehaving model could regenerate the sheet indefinitely and wedge
  // the order silently. Constructed as
  // `AgentError(CAPACITY_EXCEEDED, CHARACTER_SHEET_BUDGET_EXHAUSTED, retryable=true)`,
  // which `CATEGORY_TO_CONTEXT` projects onto `transient` — a Try Again /
  // new feedback can succeed, converting a silent infinite wedge into an
  // honest, recoverable failure.
  'CHARACTER_SHEET_BUDGET_EXHAUSTED',
  // Cross-page-consistency storyboard — emitted by the `withToolErrorBoundary`
  // wrapper when the supernode regenerates the storyboard > the
  // consecutive-regen cap (5) without ever handing off — since #916 there is
  // no human storyboard review; the hook injects the handoff itself.
  // Mirrors `CHARACTER_SHEET_BUDGET_EXHAUSTED`: the storyboard subagent is a
  // one-shot `tool()` with no internal loop and the regen loop is
  // ReAct-model-driven, so a misbehaving model could regenerate the storyboard
  // indefinitely and wedge the order silently. Constructed as
  // `AgentError(CAPACITY_EXCEEDED, STORYBOARD_BUDGET_EXHAUSTED, retryable=true)`,
  // which `CATEGORY_TO_CONTEXT` projects onto `transient` — a Try Again / new
  // feedback can succeed.
  'STORYBOARD_BUDGET_EXHAUSTED',
  // Issue #712 — emitted by the cover/book regen no-op guard when a regen
  // run "completes" but produces a page set that is BYTE-IDENTICAL to the
  // pre-regen page set (every `pages/*.png` composite md5-unchanged). This
  // is the silent no-op #712 documents: the regen counter was consumed and
  // the timer ran, but the customer got nothing new. The guard routes this
  // through the #500 non-destructive recovery (refund counter +
  // `lastRegenFailure`) so the failure is LOUD and the customer keeps their
  // (still-correct) previous book. Classified `transient` — a fresh Try
  // Again can succeed (e.g. once a transient model issue clears).
  'REGEN_PRODUCED_NO_CHANGES',
  // Issue #809 — emitted by `reviewArtifactSkill` when Gemini's response
  // still fails to parse after a lenient balanced-JSON extraction pass
  // (scan for the first balanced top-level `{...}` object, respecting
  // string literals/escapes, and retry parsing just that slice). This
  // recovers the common Sentry WOOLABY-SERVER-44 shape — valid JSON
  // followed by trailing non-whitespace garbage — which a strict
  // `JSON.parse` rejects even though the JSON value itself is fine.
  // Classified as TRANSIENT (retryable): a fresh Gemini call usually
  // produces cleanly-terminated JSON. Distinct from the existing
  // `JSON_PARSE_FAILED` (PERMANENT) reason, which stays reserved for the
  // separate Zod-validation-failure branch (valid JSON, wrong shape) and
  // the other three emitters (`generateStoryPlan`, `generatePdf`,
  // `generateCharacterSheet`) — those are unaffected by this change.
  'REVIEW_JSON_PARSE_FAILED',
  // Issue #864 — emitted by the parent `appGraph` supernode wrapper when a
  // `handoffToGeneratePages` ToolMessage was ISSUED but its content could not
  // be deserialised by `parseHandoffMessageContent` (e.g. the context-overflow
  // guard truncated the payload before #864's exemption landed). The reverse
  // handoff scan used to `continue` past such a message silently, so
  // `pageGenInputs` was never written and the graph routed to END leaving the
  // order non-terminal with `failure_reason = NULL` and nobody alerted — order
  // `ea271b19` died exactly this way with 0 of 24 pages. This is its OWN slug
  // rather than a page-gen reason on purpose: page generation never ran, and
  // relabelling would break the #727 rule that a bound decides WHEN to stop,
  // never WHAT failed. Constructed as `AgentError(TRANSIENT,
  // HANDOFF_UNPARSEABLE, retryable=true)`, which `CATEGORY_TO_CONTEXT`
  // projects onto the existing `transient` context — a fresh run re-issues a
  // readable handoff, so Try Again is the correct recovery.
  'HANDOFF_UNPARSEABLE',
  // Issue #864 — emitted by the RUNNER-LEVEL backstop
  // (`agents/authorAgent/index.ts`) when the graph returns and the order's
  // status is still non-terminal with nothing in the run explaining why it
  // ended. It is the layer of last resort behind the in-graph guards above:
  // order `ea271b19` sat in `GENERATING_FINAL` with `failure_reason = NULL`
  // for ~3 hours, so nothing alerted and the customer never learned their
  // book had died. It gets its OWN slug because every other reason names a
  // phase that actually failed, and relabelling would break the #727 rule
  // that a bound decides WHEN to stop, never WHAT failed — the backstop
  // therefore substitutes this slug ONLY when no failure envelope exists; a
  // run that DID record a failure keeps that failure's reason verbatim.
  // Constructed as `AgentError(TRANSIENT, SILENT_END, retryable=true)`,
  // which `CATEGORY_TO_CONTEXT` projects onto the existing `transient`
  // context — nothing proved the work impossible, so Try Again is the
  // honest recovery. No migration: `orders.failure_reason` is a permissive
  // `text` column and clients branch on `failure_context`.
  'SILENT_END',
  // Issue #866 — the HONEST fallback for a failure envelope we could not read.
  // Order `c78a0fe2` was permanently blocked by Azure's image safety system,
  // but the model called `failGeneration` with `reason: "content_blocked"` (a
  // `FailureContext` value, not a `FailureReason`) and a prose `context`.
  // `parsePipelineFailureError` correctly rejected both and returned `null`, so
  // the route fell back to `PAGE_GENERATION_FAILED` / `transient` — a claim
  // that a retry would help, on photographs that would be rejected again.
  // `UNCLASSIFIED` exists so that path can say "we do not know" instead of
  // naming a phase that did not fail (#727: never relabel a failure). Emitted
  // by `skills/failGeneration.ts`'s slug coercion and by the route's
  // unparseable-envelope branch. No migration — `orders.failure_reason` is a
  // permissive `text` column and clients branch on `failure_context`.
  'UNCLASSIFIED',
  // Issue #874 follow-up — emitted by the `withToolErrorBoundary` wrapper when
  // `startCoverGeneration` fails with the wrap producer's NO-TITLE refusal more
  // than `MAX_CONSECUTIVE_COVER_TITLE_REFUSALS` times in a row.
  //
  // The refusal itself (`generateOutpaintCoverSkill`, `COVER_GENERATION_FAILED`
  // / TRANSIENT / retryable) is deliberately retryable so the model can
  // re-supply `title` on the kickoff. Nothing bounded that retry: the
  // `invocations.perTool` counter in the envelope is REPORTED to the model but
  // enforces nothing, the cover subgraph carries no `retryPolicy`, and there is
  // no cover analogue of `MAX_CONSECUTIVE_CHARACTER_SHEET_REGENS`. So a model
  // that kept re-supplying a blank title drained the supernode's recursion
  // limit and died as `RECURSION_LIMIT_EXCEEDED` — the same end state, reached
  // slower and mislabelled.
  //
  // It gets its OWN slug rather than reusing `COVER_GENERATION_FAILED` for the
  // #727 reason: a bound decides WHEN to stop, never WHAT failed, and this one
  // names a specific, human-fixable cause (the book has no title in ANY durable
  // place — not the caller's argument, not `orders.book_title`, not the
  // approved plan in the transcript). Constructed as
  // `AgentError(CAPACITY_EXCEEDED, COVER_TITLE_BUDGET_EXHAUSTED,
  // retryable=true)`, which `CATEGORY_TO_CONTEXT` projects onto `transient` —
  // writing the title back onto the order and hitting Try Again succeeds. No
  // migration: `orders.failure_reason` is a permissive `text` column and
  // clients branch on `failure_context`.
  'COVER_TITLE_BUDGET_EXHAUSTED',
  // Issue #923 — emitted by the runner's finalize gate
  // (`runFinalizeArtifactGates` in `agents/authorAgent/index.ts`) ONLY on
  // positive evidence: a book has committed pages, the canonical
  // `full-cover.png` is PROVABLY absent (strict probe), the transcript has
  // NO successful `startCoverGeneration`, and the #922 cover-phase re-entry
  // budget (`MAX_COVER_PHASE_NUDGES`, counted from directives in the
  // transcript) is spent. Anything short of that keeps a non-permanent
  // verdict.
  //
  // It gets its OWN slug rather than reusing `COVER_GENERATION_FAILED` because
  // they are DIFFERENT CONDITIONS, not a bound on the same one (#727 does not
  // apply): `COVER_GENERATION_FAILED` means "the cover subagent ran and failed"
  // and has ~15 transient producers in `subagents/cover/`, so
  // `inferFailureContext` must answer `transient` for it; this one means "the
  // supernode never ran §9 at all" and a retry replays the same sign-off. Two
  // consequences of sharing the slug were latent until #923: a NULL
  // `failure_context` column re-projected the permanent verdict as retryable
  // in the status route, and `failGeneration.ts`'s transcript recovery derived
  // `transient` for it. A distinct slug makes both correct by construction.
  // PERMANENT / retryable=false; no migration (`failure_reason` is `text`).
  'COVER_PHASE_NOT_RUN',
] as const;

export type FailureReason = (typeof FAILURE_REASONS)[number];

/** Context describing the cause of a pipeline failure (single source of truth) */
export const FAILURE_CONTEXTS = [
  'content_blocked',
  'feedback_caused',
  'survey_caused',
  'transient',
  // Issue #589 — emitted when the failure is provably non-recoverable by
  // a retry (currently: Vertex AI 404 "Publisher Model … was not found"
  // for a model name that doesn't exist at the configured endpoint). The
  // universal Try Again retry path treats `permanent` as a signal that
  // surfacing "Edit My Book" is the only sensible recovery — retrying the
  // same call would deterministically reproduce the same 404.
  'permanent',
  // Issue #537 — `user_abandoned` was deleted alongside the structural-repair
  // HITL "Start over" action. No production path emits it now; the
  // recovery-planner / failure-gate / client error-block code paths that
  // branched on it have all been removed.
  //
  // Issue #866 — the context half of `UNCLASSIFIED` above: the envelope was
  // unreadable, so we know neither what failed nor whether a retry would help.
  // It is deliberately NOT `transient` (which CLAIMS a retry will help — false
  // for the safety block that motivated this) and NOT `permanent` (which
  // claims the opposite with equally little evidence). ⚠️ Adding it reverses
  // the `CATEGORY_TO_CONTEXT` docstring's stated reason for existing ("so we
  // don't have to widen the shared wire enum") — that projection maps a
  // successful CLASSIFICATION onto a context, whereas this value describes an
  // ENVELOPE we could not classify at all, so no `FailureCategory` maps to it.
  'unclassified',
] as const;

export type FailureContext = (typeof FAILURE_CONTEXTS)[number];

/**
 * Failure reason slugs written by infrastructure paths that have no LangGraph
 * state to project from (startup recovery, recovery planner, SIGTERM drain).
 * Kept separate from `FAILURE_REASONS` because those values flow through the
 * agent's typed `RepairEnvelope` schemas and must stay agent-scoped. Both
 * enums are persisted into the same `orders.failure_reason` column.
 */
export const INFRA_FAILURE_REASONS = [
  'STARTUP_NO_CHECKPOINT',
  'STARTUP_MOCK_MODE_RESUME',
  'STARTUP_RECOVERY_ERROR',
  'RECOVERY_PLANNER_UNRECOVERABLE',
  'SHUTDOWN_DRAIN_TIMEOUT',
] as const;

export type InfraFailureReason = (typeof INFRA_FAILURE_REASONS)[number];

/**
 * Story plan data shown to the user for review.
 */
export const planReviewDataSchema = z.object({
  title: z.string(),
  plotSummary: z.string(),
  pageCount: z.number(),
  bookLanguage: z.string(),
  /**
   * Server-generated UUID identifying the specific HITL interrupt fire that
   * surfaced this review payload. Optional on the read side — the projection
   * helper can be called against arbitrary state where no plan-review
   * interrupt is active. On the SUBMIT endpoint (`POST /:id/review-plan`)
   * the field is REQUIRED; the client must echo it back from the most recent
   * polling response. The client's submit helper throws if absent rather
   * than producing a 400.
   */
  interruptId: z.string().uuid().optional(),
  /**
   * LEGACY. The movie-teaser synopsis older plans carried. No plan written
   * since 2026-10-08 has one — `plotSummary` is the plan's one text (see
   * `storyPlanSchema` below). Optional so an old payload still parses.
   */
  storyTeaser: z.string().nullable().optional(),
});

export type PlanReviewData = z.infer<typeof planReviewDataSchema>;

/**
 * One obstacle in a {@link storyBibleSchema}: what is in the way, why it
 * really blocks the want, and the child's own action that gets past it.
 */
const storyBibleObstacleSchema = z.object({
  what: z.string(),
  whyItBlocks: z.string(),
  howTheChildSolvesIt: z.string(),
});

/**
 * The story bible behind a plan (plan
 * `docs/plans/2026-11-02-story-interest-and-page-fixes.md`, D1/D2): the
 * small set of story facts that make the plot interesting to a 3–5-year-old,
 * written with the story pitch and carried with the approved plan so the
 * script can realise every item.
 *
 * Every field is REQUIRED inside it, and the same object is the wire shape
 * (Azure strict output rejects optional keys). The array LENGTHS are not on
 * the schema: `placeDetails` is 2–4 and `obstacles` exactly 3, enforced by
 * code after the call (`book/steps/storyDevelopment.ts`'s
 * `normalizeStoryBible`), because a count bound on the wire is one more
 * constraint the provider may reject, and a stored bible must always parse.
 */
export const storyBibleSchema = z.object({
  /** What the child wants and why it matters to them, in a child's words. */
  want: z.string(),
  /** Who or what needs help, and what happens if nobody helps. */
  stakes: z.string(),
  /** What the survey companion adds: one personality trait, shown in an action. */
  companionRole: z.string(),
  /** Concrete things the requested setting is known for that the PLOT uses. */
  placeDetails: z.array(z.string()),
  /** Three different kinds of problem, each solved by the child's own action. */
  obstacles: z.array(storyBibleObstacleSchema),
  /** One surprise a 3–5-year-old can follow. */
  turn: z.string(),
  /** Something a small child laughs at: slapstick, a silly sound, an animal's antics. */
  funnyMoment: z.string(),
  /** Gives exactly what the want asked for; any thanks or reward names what it is for. */
  payoff: z.string(),
});

export type StoryBible = z.infer<typeof storyBibleSchema>;

/**
 * The approved story plan, persisted verbatim to `orders.approved_plan_json`
 * by the Mastra workflow on plan-review approval. Source of truth for
 * `projectPlanReviewData`, `tryAgain`'s continuation of an in-progress run,
 * and the eval seeder — all read this column, never Mastra state directly.
 */
export const storyPlanSchema = z.object({
  title: z.string().min(1),
  /**
   * LEGACY, read-only. `generatePlan` no longer asks for a teaser: the plan
   * has ONE customer-facing text, `plotSummary` (plan
   * `docs/plans/2026-10-08-plan-wait-review-backlog.md` Task 1 — the review
   * card showed the same story twice). Kept optional because staging, PR
   * previews and psss share one database, and a run suspended before that
   * change still holds a plan carrying it; a resumed run must still parse.
   * Nothing new writes it, and nothing may require it.
   */
  storyTeaser: z.string().optional(),
  plotSummary: z.string().min(1),
  pageCount: z.number().int().min(4).max(40),
  bookLanguage: z.string().min(1),
  /**
   * The story bible of the pitch this plan was written from (D2). OPTIONAL,
   * and it stays optional: every plan stored before story development, and
   * every plan a run suspended at `PLAN_REVIEW` before it, has none, and
   * staging, PR previews and psss share one database. Nothing may require
   * it and nothing may synthesize one; the script renders it only when it is
   * here. The plan review card reads only `title` / `plotSummary`.
   */
  storyBible: storyBibleSchema.optional(),
});

export type StoryPlan = z.infer<typeof storyPlanSchema>;

/**
 * Response from POST /api/orders/:id/start-generation,
 * POST /api/orders/:id/try-again and POST /api/orders/:id/start-over
 * (Mastra rewrite — `book/runs.ts`). The client observes the run via
 * `runId` against `/api/mastra/workflows/bookWorkflow/*`.
 */
export const startGenerationResponseSchema = z.object({
  runId: z.string().uuid(),
});

export type StartGenerationResponse = z.infer<
  typeof startGenerationResponseSchema
>;

/**
 * Character sheet data shown to the user for review.
 * storagePath is persisted; imageUrl is generated on-demand from storagePath.
 */
export const characterSheetReviewDataSchema = z.object({
  storagePath: z.string(),
  imageUrl: z.string().optional(),
  /**
   * Server-generated UUID identifying the specific HITL interrupt fire that
   * surfaced this review payload. Optional on the read side — the projection
   * helper can be called against arbitrary state where no CS-review interrupt
   * is active. On the SUBMIT endpoint (`POST /:id/review-character-sheet`)
   * the field is REQUIRED; the client must echo it back from the most recent
   * polling response. The client's submit helper throws if absent rather
   * than producing a 400.
   */
  interruptId: z.string().uuid().optional(),
});

export type CharacterSheetReviewData = z.infer<
  typeof characterSheetReviewDataSchema
>;

/**
 * Loading-stage payload for the frontend-driven, time-based loading bar
 * (`docs/LOADING.md`). Surfaced additively on every polling response so the
 * client can compute the bar percent purely from elapsed time without an
 * extra round-trip.
 *
 * `stage` is intentionally `z.string()` rather than a closed enum — the
 * server may add new stages over time and clients must tolerate unknown
 * slugs (per the project's permissive wire-schema rule for fields the
 * server may extend). The server's narrow `LoadingStage` union lives in
 * `apps/server/src/services/loadingStages.ts`.
 *
 * `null` when the order is in a terminal state (`PREVIEW_READY`,
 * `GENERATION_FAILED`, `PRINT_JOB_SUBMITTED`, `SHIPPED`, etc.) or before
 * generation has started (`PAID`, `INIT`, etc.). The client treats
 * `loadingStage === null` as "no active wait — render the next screen".
 */
export const loadingStagePayloadSchema = z.object({
  stage: z.string(),
  averageLoadingSeconds: z.number(),
  startedAt: z.string().datetime({ offset: true }),
});

export type LoadingStagePayload = z.infer<typeof loadingStagePayloadSchema>;

/**
 * Wire schema for the structured failure-details payload (issue #677). Mirrors
 * `FailureDetails` from `./orderProjection.ts` but lives here so the polling
 * response can validate it through Zod. Every field is optional so legacy
 * failure paths (infra failures, pre-AgentError envelopes) round-trip cleanly.
 *
 * `pageFailureDetailSchema.lastReason` is intentionally open (`z.string()`) —
 * the per-page recovery path may emit free-form hints that are NOT in
 * `FAILURE_REASONS`; clients display this verbatim and never branch on it.
 */
export const pageFailureDetailSchema = z.object({
  pageId: z.string(),
  retries: z.number(),
  lastReason: z.string(),
  forceAccepted: z.boolean(),
});

/**
 * The `failGeneration` slug-coercion audit (#866 / #927). `field` is
 * deliberately `z.string()` — the server may name a new slot without bumping
 * any client, and no client branches on it.
 */
export const failGenerationCoercionDetailsSchema = z.object({
  coercions: z.array(z.object({ field: z.string(), received: z.string() })),
  recoveredReason: z.string().optional(),
  recoveredFromTool: z.string().optional(),
  recoveredFromCategory: z.string().optional(),
});

export const failureDetailsSchema = z.object({
  pages: z.array(pageFailureDetailSchema).optional(),
  globalRegenCount: z.number().optional(),
  lastSkillFailed: z.string().optional(),
  failGeneration: failGenerationCoercionDetailsSchema.optional(),
}) satisfies z.ZodType<FailureDetails>;

/**
 * Response from GET /api/orders/:id/generation-status
 * Zod enums derived from FAILURE_REASONS/FAILURE_CONTEXTS arrays (single source of truth).
 */
export const generationStatusResponseSchema = z.object({
  step: z.enum(GENERATION_STEPS),
  /**
   * Failure slug. Accepts both agent failure reasons (`FAILURE_REASONS`) and
   * infra failure reasons (`INFRA_FAILURE_REASONS`). Wire format intentionally
   * loose — `z.string()` — so the server can introduce new slugs without
   * breaking older clients (clients display `failureMessage` for human copy).
   */
  failureReason: z.string().nullable().optional(),
  /** Context describing the cause of the failure (feedback, survey, transient) */
  failureContext: z.enum(FAILURE_CONTEXTS).nullable().optional(),
  /** AI-generated user-facing message explaining what triggered a content block */
  failureMessage: z.string().nullable().optional(),
  /**
   * Issue #677 — recovery class (e.g. `PERMANENT`, `TRANSIENT`, `SAFETY`,
   * `COUNT_MISMATCH`, `CAPACITY_EXCEEDED`) from the `AgentError` taxonomy.
   * Null for legacy failures that predate the taxonomy. Wire format open
   * (`z.string()`) per line 80 convention — the server can introduce new
   * categories without breaking older clients.
   */
  failureCategory: z.string().nullable().optional(),
  /**
   * Issue #677 — structured failure diagnostics (per-page retry audit,
   * global regen counter, last skill name). Null for legacy failures and
   * for infra failures that don't carry agent-side structure.
   */
  failureDetails: failureDetailsSchema.nullable().optional(),
  planReview: planReviewDataSchema.nullable(),
  characterSheetReview: characterSheetReviewDataSchema.nullable(),
  currentPage: z.number().nullable(),
  totalPages: z.number().nullable(),
  /** True when book artifacts are uploaded and the preview page is available */
  previewAvailable: z.boolean().optional(),
  /**
   * LangGraph thread id for this generation run. Surfaced to the client so
   * the failure UI can display it as part of the technical-details payload
   * (issue #493 Task 16, B8). Null when no thread has been allocated yet
   * (pre-generation states).
   */
  threadId: z.string().nullable().optional(),
  /**
   * Failure metadata from a regeneration attempt that failed and was
   * restored from backup (issue #500). Surfaces the dismissible banner on
   * `/book/preview/<id>` so the user knows the previous run failed without
   * destroying their book. Null/undefined when there is no pending regen
   * failure to surface.
   */
  lastRegenFailure: lastRegenFailureSchema.nullable().optional(),
  /**
   * LEGACY. `orders.story_teaser`, which nothing writes since 2026-10-08
   * (the plan's one text is `plotSummary`, `storyPlanSchema`). The column
   * is kept — it still holds older orders' teasers — so the field stays
   * optional and nullable on the wire.
   */
  storyTeaser: z.string().nullable().optional(),
  /**
   * Final book title written by the author-agent (spec §17 / `docs/LOADING.md`).
   * Mirrored to `orders.book_title` on plan approval by `applyReviewSubmission`
   * (AI-brain rewrite finalization Task 7) so the loading UI can render
   * "Generating <title>..." once known. Surfaced additively on every polling
   * response — older clients ignore it; newer clients display the title
   * during the post-approval generation phases. Null on legacy graph orders
   * and pre-approval reads.
   */
  bookTitle: z.string().nullable().optional(),
  /**
   * Frontend-driven loading-bar input (`docs/LOADING.md`). The server
   * tells the client which stage the order is in, the typical wait time
   * for that stage, and when the wait started. The client animates the
   * bar 0 → 86 → 99 against `averageLoadingSeconds` and jumps to 100 on
   * a stage transition.
   *
   * Null when the order is in a terminal state or before generation has
   * started.
   */
  loadingStage: loadingStagePayloadSchema.nullable(),
});

export type GenerationStatusResponse = z.infer<
  typeof generationStatusResponseSchema
>;

/**
 * Request body for POST /api/orders/:id/review-plan
 */
export const reviewPlanRequestSchema = z.object({
  approved: z.boolean(),
  feedback: z.string().optional(),
  /**
   * Server-generated UUID identifying the specific HITL interrupt fire the
   * client is responding to. The client echoes back the value it received in
   * `PlanReviewData.interruptId`.
   */
  interruptId: z.string().uuid(),
  /**
   * Client-generated UUID, stable across the lifetime of a single review form
   * (Phase 4, round-5 M3 + round-6 R6B-H1). Echoed back on every retry of the
   * same form so accidental double-POST collapses to a single durable row.
   * Optional on the wire while the client rolls out — when missing, the
   * server fills in a fresh `randomUUID()` so each submit still gets a unique
   * dedup key against the per-(thread, kind, interrupt_id) index.
   */
  reviewSubmissionId: z.string().uuid().optional(),
});

export type ReviewPlanRequest = z.infer<typeof reviewPlanRequestSchema>;

/**
 * Response from POST /api/orders/:id/review-plan
 */
export const reviewPlanResponseSchema = z.object({
  success: z.boolean(),
});

export type ReviewPlanResponse = z.infer<typeof reviewPlanResponseSchema>;

/**
 * Request body for POST /api/orders/:id/review-character-sheet
 *
 * Phase 1 split (`pageBorderStyleOverride` → typed enum + freeform feedback):
 *   - `pageBorderStyleId: PageBorderStyle | null` — typed enum override that
 *     mutates `surveyData.preferences.pageBorderStyle`.
 *   - `humanCharacterSheetFeedback: string | null` — freeform regen feedback
 *     consumed by the prompt builder. NOT concatenated into the typed enum.
 *
 * Backward compatibility: `borderStyle` (single string field) is still
 * accepted from older clients. The route handler dispatches it: when its
 * value matches a known `PAGE_BORDER_STYLES` entry, it is treated as
 * `pageBorderStyleId`; otherwise it is treated as
 * `humanCharacterSheetFeedback`. New explicit fields override `borderStyle`
 * when both are present.
 */
export const reviewCharacterSheetRequestSchema = z
  .object({
    approved: z.boolean(),
    feedback: z.string().optional(),
    /** Typed enum override for the survey's `pageBorderStyle`. */
    pageBorderStyleId: z.enum(PAGE_BORDER_STYLES).nullable().optional(),
    /**
     * Freeform regen feedback. Flows into the regen prompt builder as a
     * separate slot — never concatenated with the typed `pageBorderStyleId`.
     */
    humanCharacterSheetFeedback: z
      .string()
      .trim()
      .min(1)
      .max(500)
      .nullable()
      .optional(),
    /**
     * Legacy field retained for backward compatibility with older clients.
     * Server dispatches: enum match → pageBorderStyleId; non-match →
     * humanCharacterSheetFeedback.
     */
    borderStyle: z.string().trim().min(1).max(500).optional(),
    /**
     * Issue #765 — which review surface produced the rejection feedback.
     * `'character'` (Request Changes textarea) | `'border'` (Request
     * Different Border textarea) today; OPEN string (no closed enum) so the
     * server can introduce new scopes without bumping every client. Bounded
     * like the freeform feedback fields. Only meaningful when
     * `approved === false` (enforced by the refine below). The skill renders
     * a scope-aware prompt block only for the known values and ignores the
     * rest — the value is never interpolated verbatim into prompts.
     */
    feedbackScope: z.string().trim().min(1).max(50).optional(),
    /**
     * Server-generated UUID identifying the specific HITL interrupt fire the
     * client is responding to. The client echoes back the value it received
     * in `CharacterSheetReviewData.interruptId`.
     */
    interruptId: z.string().uuid(),
    /**
     * Client-generated UUID, stable across the lifetime of a single review
     * form (Phase 4, round-5 M3 + round-6 R6B-H1). Echoed back on every
     * retry of the same form so accidental double-POST collapses to a single
     * durable row. Optional on the wire while the client rolls out — when
     * missing, the server fills in a fresh `randomUUID()` so each submit
     * still gets a unique dedup key against the per-(thread, kind,
     * interrupt_id) index.
     */
    reviewSubmissionId: z.string().uuid().optional(),
  })
  .refine(
    (data) => !((data.borderStyle || data.pageBorderStyleId) && data.approved),
    {
      message:
        'border-style override can only be provided when approved is false',
      path: ['pageBorderStyleId'],
    }
  )
  // Issue #765 — same rejection-only rule as the border fields above:
  // `feedbackScope` qualifies rejection feedback, so it is invalid on an
  // approval.
  .refine((data) => !(data.feedbackScope && data.approved), {
    message: 'feedbackScope can only be provided when approved is false',
    path: ['feedbackScope'],
  });

export type ReviewCharacterSheetRequest = z.infer<
  typeof reviewCharacterSheetRequestSchema
>;

/**
 * Response from POST /api/orders/:id/review-character-sheet
 */
export const reviewCharacterSheetResponseSchema = z.object({
  success: z.boolean(),
});

export type ReviewCharacterSheetResponse = z.infer<
  typeof reviewCharacterSheetResponseSchema
>;
